import { useState, type ReactNode } from "react";
import { Eye, EyeOff, Pencil, Trash2, Plus, RotateCcw, GripVertical, Undo2 } from "lucide-react";
import AdminFieldModal, { type FieldDef } from "./AdminFieldModal";
import type { ManagedCollection, ManagedItem } from "./useManagedCollection";
import { dragDebug } from "./dragDebug";

// Pointer-drag highlight: the card element currently under the cursor.
let HILITE: HTMLElement | null = null;
function setHilite(el: HTMLElement | null) {
  if (HILITE === el) return;
  if (HILITE) {
    HILITE.style.outline = "";
    HILITE.style.outlineOffset = "";
    HILITE.style.borderRadius = "";
  }
  HILITE = el;
  if (HILITE) {
    HILITE.style.outline = "2px solid #f59e0b";
    HILITE.style.outlineOffset = "2px";
    HILITE.style.borderRadius = "12px";
  }
}

// Optional cross-collection drag config. When a page renders cards from several
// managed collections into one visual grid (e.g. Restaurants), pass a shared
// `group` so the drop hit-test spans all of them, and an `onReorder` that knows
// how to reposition across collections (the default same-collection swapItems
// can't move a card between two different mc stores).
interface DragOpts {
  group?: string;
  onReorder?: (draggedId: string, targetId: string, before: boolean) => void | Promise<void>;
}

// Shared pointer-drag reorder logic, used by both AdminItem (full overlay) and
// AdminReorder (drag-only). Returns props to spread on the card wrapper.
function useCardDrag(mc: ManagedCollection<any>, itemMcId: string, opts: DragOpts = {}) {
  const [dragging, setDragging] = useState(false);

  const startDrag = (e: React.PointerEvent) => {
    if (e.button !== 0) return; // primary button only
    // Only bail for real form controls (typing/selecting). We DELIBERATELY allow
    // drags to begin on links & buttons — most category-page cards are mostly a
    // "Visit Website" button + social/maps links, so bailing on <a>/<button> made
    // the card undraggable except for a sliver of image. A real drag is
    // distinguished from a tap by the 6px movement threshold below, and the
    // click that follows a real drag is suppressed on pointerup — so taps still
    // navigate normally while drags reorder.
    if ((e.target as HTMLElement).closest("input,textarea,select,[data-mc-no-drag]")) return;
    // Stop the event bubbling to an ANCESTOR draggable wrapper (e.g. a card lives
    // inside a draggable "section"); otherwise both would drag at once and the
    // ancestor's transform would shift the cards, breaking the drop hit-test.
    e.stopPropagation();

    const el = e.currentTarget as HTMLElement;
    // NOTE: pointer capture is taken only once a real drag STARTS (in onMove
    // past the 6px threshold), NOT on pointerdown. Capturing on press retargets
    // the follow-up `click` to this wrapper, which swallowed navigation on card
    // links/buttons (View All, header nav, category tiles) — a plain tap must
    // still click through to the link. Deferring capture keeps taps working
    // while still capturing for the duration of an actual drag.
    const startX = e.clientX;
    const startY = e.clientY;
    let active = false;
    // When a cross-collection `group` is set, the drop pool spans every card in
    // that group (all collections); otherwise it's just this collection.
    const SEL = opts.group
      ? `[data-mc-card][data-mc-group="${opts.group}"]`
      : `[data-mc-card][data-mc-col="${mc.slug}"]`;

    // Nearest-card hit-test: the card whose CENTER is closest to the cursor
    // (excluding the dragged one). Using nearest-center rather than "rect
    // contains cursor" means drops that land in the gap between cards, or near an
    // edge, still resolve to a sensible target — so placement is always defined.
    const cardUnder = (x: number, y: number): HTMLElement | null => {
      const all = Array.from(document.querySelectorAll(SEL)) as HTMLElement[];
      let best: HTMLElement | null = null;
      let bestDist = Infinity;
      for (const c of all) {
        if (c === el) continue;
        const r = c.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const d = (x - cx) ** 2 + (y - cy) ** 2;
        if (d < bestDist) {
          bestDist = d;
          best = c;
        }
      }
      return best;
    };

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!active) {
        if (Math.hypot(dx, dy) < 6) return;
        active = true;
        // Real drag started — NOW take pointer capture so the rest of the move/up
        // stream is delivered here (also works inside the HappySpace editor's
        // overlay). Deferred to here so plain taps aren't captured (they must
        // click through to their link/button).
        try { el.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
        setDragging(true);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
        el.style.position = "relative";
        el.style.zIndex = "1000";
        el.style.transition = "none";
        el.style.opacity = "0.9";
        el.style.boxShadow = "0 12px 30px rgba(0,0,0,0.25)";
        dragDebug(`dragging ${itemMcId}…`, "#7c3aed");
      }
      el.style.transform = `translate(${dx}px, ${dy}px) scale(1.02)`;
      const target = cardUnder(ev.clientX, ev.clientY);
      setHilite(target && target.getAttribute("data-mc-id") !== itemMcId ? target : null);
    };

    const reset = () => {
      el.style.transform = "";
      el.style.position = "";
      el.style.zIndex = "";
      el.style.pointerEvents = "";
      el.style.transition = "";
      el.style.opacity = "";
      el.style.boxShadow = "";
    };

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", onUp, true);
      window.removeEventListener("pointercancel", onUp, true);
      try { el.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      const poolCount = document.querySelectorAll(SEL).length;
      const target = active ? cardUnder(ev.clientX, ev.clientY) : null;
      reset();
      setDragging(false);
      setHilite(null);
      // A real drag just happened — swallow the click that the browser fires next
      // so dragging a card that is (or contains) a link/button doesn't also
      // navigate or submit. A plain tap (no drag) never reaches here as active,
      // so normal clicks still work.
      if (active) {
        const swallow = (ce: Event) => {
          ce.preventDefault();
          ce.stopPropagation();
          window.removeEventListener("click", swallow, true);
        };
        window.addEventListener("click", swallow, true);
        setTimeout(() => window.removeEventListener("click", swallow, true), 400);
      }
      // SWAP: drop A onto B → they trade places; all other cards stay put.
      const tid = target?.getAttribute("data-mc-id");
      if (!active) {
        dragDebug("drop ignored — no movement", "#9ca3af");
        return;
      }
      if (!tid || tid === itemMcId) {
        dragDebug(`drop: no target (${mc.slug}, pool=${poolCount})`, "#b45309");
        return;
      }
      // `before` = dropped on the leading half of the target (its left edge in a
      // grid), so we can insert the dragged card just before vs after it.
      const trect = target!.getBoundingClientRect();
      const before = ev.clientX < trect.left + trect.width / 2;
      if (opts.onReorder) {
        // Cross-collection (or unified) reorder driven by the page.
        dragDebug(`moving…\n${itemMcId}\n→ ${before ? "before" : "after"} ${tid}`, "#082f6d");
        Promise.resolve(opts.onReorder(itemMcId, tid, before))
          .then(() => dragDebug(`✓ move saved\n${itemMcId} → ${tid}`, "#16a34a"))
          .catch((err: any) => dragDebug(`✗ move FAILED\n${err?.message || err}`, "#dc2626"));
        return;
      }
      dragDebug(`swapping…\n${itemMcId}\n⇅ ${tid}`, "#082f6d");
      Promise.resolve(mc.swapItems(itemMcId, tid))
        .then(() => dragDebug(`✓ swap saved\n${itemMcId} ⇅ ${tid}`, "#16a34a"))
        .catch((err: any) => dragDebug(`✗ swap FAILED\n${err?.message || err}`, "#dc2626"));
    };

    // Capture phase on window → we receive move/up before any editor overlay.
    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);
  };

  const rootProps = {
    "data-mc-card": "",
    "data-mc-id": itemMcId,
    "data-mc-col": mc.slug,
    ...(opts.group ? { "data-mc-group": opts.group } : {}),
    onPointerDown: startDrag,
    onDragStart: (e: React.DragEvent) => e.preventDefault(), // kill native image/link drag
    style: { touchAction: "none" as const },
  };

  return { dragging, rootProps };
}

/**
 * AdminReorder — a DRAG-ONLY wrapper (no hide/edit/delete). For places that
 * already have their own management UI but just need drag-reordering, e.g. the
 * homepage category tiles or header nav.
 */
export function AdminReorder({
  mc,
  item,
  children,
}: {
  mc: ManagedCollection<any>;
  item: ManagedItem;
  children: ReactNode;
}) {
  const { dragging, rootProps } = useCardDrag(mc, item._mcId);
  if (!mc.adminMode) return <>{children}</>;
  return (
    <div
      {...rootProps}
      className={`relative group cursor-grab active:cursor-grabbing transition-all ${dragging ? "opacity-40" : ""}`}
    >
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-40 opacity-0 group-hover:opacity-100 transition-opacity">
        <span
          className="inline-flex items-center justify-center bg-[#082f6d] text-white p-1 rounded-full shadow-lg"
          title="Drag to reorder"
        >
          <GripVertical className="w-3 h-3" />
        </span>
      </div>
      {children}
    </div>
  );
}

/**
 * AdminItem — wraps a single public card. In public mode it renders the card
 * untouched (the hook has already filtered hidden items out). In admin mode it
 * overlays hover controls (hide / edit / delete / restore) and dims + badges
 * hidden or removed items.
 */
export function AdminItem({
  mc,
  item,
  fields,
  title,
  children,
  dragGroup,
  onReorder,
  showResetToDefault,
}: {
  mc: ManagedCollection<any>;
  item: ManagedItem;
  fields: FieldDef[];
  title?: string;
  children: ReactNode;
  // Optional cross-collection drag: pass a shared group + reorder handler when
  // this card lives in a grid mixing several managed collections (Restaurants).
  dragGroup?: string;
  onReorder?: (draggedId: string, targetId: string, before: boolean) => void | Promise<void>;
  // Opt-in "Reset to default" hover control — clears any saved override for a
  // default (non-custom) card and falls back to the code default. Off by
  // default; pass true only where product wants it (currently: Kids page).
  showResetToDefault?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const { dragging, rootProps } = useCardDrag(mc, item._mcId, { group: dragGroup, onReorder });

  if (!mc.adminMode) return <>{children}</>;

  const dimmed = item._hidden || item._removed;

  return (
    <div
      {...rootProps}
      className={`relative group cursor-grab active:cursor-grabbing transition-all ${
        dimmed ? "opacity-50 grayscale" : ""
      } ${dragging ? "opacity-40" : ""}`}
    >
      {/* Status badge */}
      {(item._hidden || item._removed) && (
        <div className="absolute top-2 left-2 z-30 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
          {item._removed ? "DELETED" : "HIDDEN"}
        </div>
      )}
      {item._isCustom && !item._removed && (
        <div className="absolute top-2 right-2 z-30 bg-[#51ba4f] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
          ADDED
        </div>
      )}

      {/* Hover controls */}
      <div data-mc-no-drag className="absolute -top-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Drag handle (the whole card is draggable; this is the affordance) */}
        <span
          className="inline-flex items-center justify-center bg-[#082f6d] text-white p-1.5 rounded-full shadow-lg cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
        {item._removed ? (
          <button
            onClick={() => mc.restoreItem(item)}
            className="bg-white border border-gray-200 px-2 py-1.5 rounded-full hover:bg-green-50 shadow-lg flex items-center gap-1 text-[11px] font-semibold text-green-700"
            title="Restore item"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restore
          </button>
        ) : (
          <>
            <button
              onClick={() => mc.toggleHidden(item)}
              className="bg-white border border-gray-200 p-1.5 rounded-full hover:bg-gray-50 shadow-lg"
              title={item._hidden ? "Unhide (show publicly)" : "Hide from public"}
            >
              {item._hidden ? <EyeOff className="w-3.5 h-3.5 text-gray-400" /> : <Eye className="w-3.5 h-3.5 text-gray-600" />}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="bg-white border border-gray-200 p-1.5 rounded-full hover:bg-blue-50 shadow-lg"
              title="Edit item"
            >
              <Pencil className="w-3.5 h-3.5 text-blue-600" />
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="bg-white border border-gray-200 p-1.5 rounded-full hover:bg-red-50 shadow-lg"
              title="Delete item"
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
            {showResetToDefault && !item._isCustom && (
              <button
                onClick={() => setConfirmReset(true)}
                className="bg-white border border-gray-200 p-1.5 rounded-full hover:bg-amber-50 shadow-lg"
                title="Reset to default"
              >
                <Undo2 className="w-3.5 h-3.5 text-amber-600" />
              </button>
            )}
          </>
        )}
      </div>

      {children}

      <AdminFieldModal
        open={editing}
        title={title ? `Edit ${title}` : "Edit item"}
        fields={fields}
        initial={item}
        onSave={(vals) => mc.saveEdit(item, vals)}
        onClose={() => setEditing(false)}
      />

      {confirmReset && (
        <div
          data-mc-no-drag
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmReset(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reset to default?</h3>
            <p className="text-sm text-gray-500 mb-4">
              This discards any edits made to this card (image, name, address, etc.) and restores the original values.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmReset(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await mc.resetToDefault(item);
                  setConfirmReset(false);
                }}
                className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          data-mc-no-drag
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmDelete(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete this item?</h3>
            <p className="text-sm text-gray-500 mb-4">
              {item._isCustom
                ? "This admin-added item will be permanently removed."
                : "It will be removed from the public site. You can restore it later from admin mode."}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await mc.deleteItem(item);
                  setConfirmDelete(false);
                }}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * AdminAddBar — an "Add" button shown at the top of a managed section in admin
 * mode. Opens the schema modal and inserts a new custom item.
 */
export function AdminAddBar({
  mc,
  fields,
  label = "Add item",
  className = "",
}: {
  mc: ManagedCollection<any>;
  fields: FieldDef[];
  label?: string;
  className?: string;
}) {
  const [adding, setAdding] = useState(false);
  if (!mc.adminMode) return null;

  return (
    <div className={`mb-4 flex items-center gap-3 ${className}`}>
      <button
        onClick={() => setAdding(true)}
        className="inline-flex items-center gap-1.5 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[13px] font-semibold px-4 py-2 rounded-lg shadow-sm transition-colors"
      >
        <Plus className="w-4 h-4" />
        {label}
      </button>
      <span className="text-[12px] text-gray-400">
        {mc.visibleCount} visible · {mc.totalCount} total
      </span>
      <AdminFieldModal
        open={adding}
        title={label}
        fields={fields}
        onSave={(vals) => mc.addItem(vals)}
        onClose={() => setAdding(false)}
      />
    </div>
  );
}
